/*
 * Zerene staff analytics mode
 *
 * Provides the browser-side state used to distinguish normal visitors from
 * Zerene staff. Staff enrollment is persistent, while Debug is a temporary
 * override that automatically falls back to Internal. The public API is
 * exposed on window.ZereneAnalyticsMode so the settings page, status
 * indicator, and analytics bootstrap can all use the same state rules.
 *
 * Phase 3C publishes the normalized mode (`none`, `internal`, or `debug`) to
 * the GTM dataLayer before GTM loads and keeps that value synchronized when
 * staff change modes. The settings page also mirrors the session-only visual
 * indicator state so users can restore or hide the reminder without DevTools.
 * GA4 filtering itself is configured separately in GTM/GA4.
 */

(() => {
  if (window.ZereneAnalyticsMode) return;

  const CONFIG = Object.freeze({
    STAFF_COOKIE: 'zerene_analytics_staff',
    DEBUG_UNTIL_COOKIE: 'zerene_analytics_debug_until',
    STAFF_MAX_AGE_DAYS: 365,
    DEBUG_DURATION_HOURS: 4,
    MODE_CHANGE_EVENT: 'zerene:analytics-mode-change',
    INDICATOR_VISIBILITY_EVENT: 'zerene:analytics-indicator-visibility-change',
    INDICATOR_DISMISS_KEY: 'zerene_analytics_indicator_dismissed',
    DATALAYER_MODE_KEY: 'zerene_analytics_mode',
    DATALAYER_CHANGE_EVENT: 'zerene_analytics_mode_change',
  });

  const STAFF_MAX_AGE_SECONDS = CONFIG.STAFF_MAX_AGE_DAYS * 24 * 60 * 60;
  const DEBUG_DURATION_MS = CONFIG.DEBUG_DURATION_HOURS * 60 * 60 * 1000;

  function readCookie(name) {
    const prefix = `${encodeURIComponent(name)}=`;
    const parts = document.cookie.split(';');

    for (const part of parts) {
      const cookie = part.trim();
      if (cookie.startsWith(prefix)) {
        return decodeURIComponent(cookie.substring(prefix.length));
      }
    }

    return null;
  }

  function writeCookie(name, value, maxAgeSeconds) {
    document.cookie = [
      `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
      'Path=/',
      `Max-Age=${maxAgeSeconds}`,
      'SameSite=Lax',
      'Secure',
    ].join('; ');
  }

  function clearCookie(name) {
    document.cookie = [
      `${encodeURIComponent(name)}=`,
      'Path=/',
      'Max-Age=0',
      'SameSite=Lax',
      'Secure',
    ].join('; ');
  }

  function getDebugUntil() {
    const raw = readCookie(CONFIG.DEBUG_UNTIL_COOKIE);
    if (!raw) return null;

    const timestamp = Number(raw);
    if (!Number.isFinite(timestamp)) {
      clearCookie(CONFIG.DEBUG_UNTIL_COOKIE);
      return null;
    }

    return timestamp;
  }

  function getState() {
    const isStaff = readCookie(CONFIG.STAFF_COOKIE) === '1';

    if (!isStaff) {
      return {
        enrolled: false,
        mode: 'none',
        debugUntil: null,
      };
    }

    const debugUntil = getDebugUntil();

    if (debugUntil && debugUntil > Date.now()) {
      return {
        enrolled: true,
        mode: 'debug',
        debugUntil,
      };
    }

    // A stale/expired Debug cookie should never make a staff browser appear
    // to be a normal customer. Clean it up and fall back to Internal.
    if (debugUntil) {
      clearCookie(CONFIG.DEBUG_UNTIL_COOKIE);
    }

    return {
      enrolled: true,
      mode: 'internal',
      debugUntil: null,
    };
  }

  function syncDataLayerState(state, eventName) {
    window.dataLayer = window.dataLayer || [];

    const payload = {
      [CONFIG.DATALAYER_MODE_KEY]: state.mode,
    };

    if (eventName) {
      payload.event = eventName;
    }

    window.dataLayer.push(payload);
  }

  function publishState(state) {
    // Keep GTM's data model synchronized with the cookie-backed source of truth.
    syncDataLayerState(state, CONFIG.DATALAYER_CHANGE_EVENT);

    // Also notify other storefront components without coupling them to GTM.
    window.dispatchEvent(
      new CustomEvent(CONFIG.MODE_CHANGE_EVENT, {
        detail: state,
      })
    );
  }

  function enableInternal() {
    writeCookie(CONFIG.STAFF_COOKIE, '1', STAFF_MAX_AGE_SECONDS);
    clearCookie(CONFIG.DEBUG_UNTIL_COOKIE);

    const state = getState();
    publishState(state);
    return state;
  }

  function enableDebug() {
    writeCookie(CONFIG.STAFF_COOKIE, '1', STAFF_MAX_AGE_SECONDS);

    const debugUntil = Date.now() + DEBUG_DURATION_MS;
    writeCookie(
      CONFIG.DEBUG_UNTIL_COOKIE,
      String(debugUntil),
      Math.ceil(DEBUG_DURATION_MS / 1000)
    );

    const state = getState();
    publishState(state);
    return state;
  }

  function clearSettings() {
    clearCookie(CONFIG.STAFF_COOKIE);
    clearCookie(CONFIG.DEBUG_UNTIL_COOKIE);
    clearDismissedMode();

    const state = getState();
    publishState(state);
    notifyIndicatorVisibilityChange();
    return state;
  }

  // Expose one shared namespace/API instead of making page scripts duplicate
  // the cookie names, durations, or state interpretation logic.
  window.ZereneAnalyticsMode = Object.freeze({
    CONFIG,
    getState,
    enableInternal,
    enableDebug,
    clearSettings,
  });

  // theme.liquid loads this file synchronously before GTM. Pushing the current
  // state now means GTM sees the normalized mode from its very first event.
  syncDataLayerState(getState());

  function readDismissedMode() {
    try {
      return window.sessionStorage.getItem(CONFIG.INDICATOR_DISMISS_KEY);
    } catch (error) {
      return null;
    }
  }

  function dismissMode(mode) {
    try {
      window.sessionStorage.setItem(CONFIG.INDICATOR_DISMISS_KEY, mode);
    } catch (error) {
      // If sessionStorage is unavailable, dismissal simply lasts until the next render.
    }
  }

  function clearDismissedMode() {
    try {
      window.sessionStorage.removeItem(CONFIG.INDICATOR_DISMISS_KEY);
    } catch (error) {
      // If sessionStorage is unavailable there is no persisted dismissal to clear.
    }
  }

  function notifyIndicatorVisibilityChange() {
    window.dispatchEvent(new CustomEvent(CONFIG.INDICATOR_VISIBILITY_EVENT));
  }

  function initSettingsPage() {
    const root = document.querySelector('[data-zerene-analytics-settings]');
    if (!root) return;

    const status = root.querySelector('[data-zerene-analytics-status]');
    const save = root.querySelector('[data-zerene-analytics-save]');
    const clear = root.querySelector('[data-zerene-analytics-clear]');
    const indicatorToggle = root.querySelector('[data-zerene-analytics-indicator-toggle]');
    const radios = root.querySelectorAll('input[name="zerene-analytics-mode"]');
    const debugDurationTargets = root.querySelectorAll('[data-zerene-debug-duration-hours]');

    debugDurationTargets.forEach((element) => {
      element.textContent = String(CONFIG.DEBUG_DURATION_HOURS);
    });

    function setSelectedMode(mode) {
      radios.forEach((radio) => {
        radio.checked = radio.value === mode;
      });
    }

    function renderState() {
      const state = getState();

      if (indicatorToggle) {
        indicatorToggle.disabled = !state.enrolled;
        indicatorToggle.checked = state.enrolled && readDismissedMode() !== state.mode;
      }

      if (!state.enrolled) {
        status.textContent = 'This browser is not currently configured for Zerene staff analytics.';
        status.dataset.mode = 'none';
        setSelectedMode('internal');
        save.textContent = 'Enable Zerene Analytics Mode';
        return;
      }

      save.textContent = 'Save Analytics Mode';

      if (state.mode === 'debug') {
        const expires = new Date(state.debugUntil);
        status.textContent = `DEBUG mode active until ${expires.toLocaleTimeString([], {
          hour: 'numeric',
          minute: '2-digit',
        })}.`;
        status.dataset.mode = 'debug';
        setSelectedMode('debug');
        return;
      }

      status.textContent = 'INTERNAL mode active.';
      status.dataset.mode = 'internal';
      setSelectedMode('internal');
    }

    save.addEventListener('click', () => {
      const selected = root.querySelector('input[name="zerene-analytics-mode"]:checked');
      if (!selected) return;

      if (selected.value === 'debug') {
        enableDebug();
      } else {
        enableInternal();
      }

      renderState();
    });

    clear.addEventListener('click', () => {
      const confirmed = window.confirm(
        'Clear Zerene staff analytics settings from this browser? Future activity will be treated as normal customer traffic until this browser is enrolled again.'
      );

      if (!confirmed) return;
      clearSettings();
      renderState();
    });

    if (indicatorToggle) {
      indicatorToggle.addEventListener('change', () => {
        const state = getState();
        if (!state.enrolled) {
          renderState();
          return;
        }

        if (indicatorToggle.checked) {
          clearDismissedMode();
        } else {
          dismissMode(state.mode);
        }

        notifyIndicatorVisibilityChange();
        renderState();
      });
    }

    window.addEventListener(CONFIG.MODE_CHANGE_EVENT, renderState);
    window.addEventListener(CONFIG.INDICATOR_VISIBILITY_EVENT, renderState);
    renderState();
  }

  function initIndicator() {
    const indicator = document.querySelector('[data-zerene-analytics-indicator]');
    if (!indicator) return;

    const modeLabel = indicator.querySelector('[data-zerene-analytics-indicator-mode]');
    const close = indicator.querySelector('[data-zerene-analytics-indicator-close]');
    let expiryTimer = null;

    function scheduleDebugExpiry(state) {
      if (expiryTimer) {
        window.clearTimeout(expiryTimer);
        expiryTimer = null;
      }

      if (state.mode !== 'debug' || !state.debugUntil) return;

      const delay = Math.max(0, state.debugUntil - Date.now() + 100);
      expiryTimer = window.setTimeout(() => {
        const nextState = getState();
        publishState(nextState);
        renderIndicator();
      }, delay);
    }

    function renderIndicator() {
      const state = getState();
      scheduleDebugExpiry(state);

      if (!state.enrolled) {
        indicator.hidden = true;
        indicator.dataset.mode = 'none';
        return;
      }

      indicator.dataset.mode = state.mode;
      modeLabel.textContent = state.mode.toUpperCase();
      indicator.hidden = readDismissedMode() === state.mode;
    }

    close.addEventListener('click', () => {
      const state = getState();
      if (!state.enrolled) return;

      dismissMode(state.mode);
      notifyIndicatorVisibilityChange();
    });

    window.addEventListener(CONFIG.MODE_CHANGE_EVENT, renderIndicator);
    window.addEventListener(CONFIG.INDICATOR_VISIBILITY_EVENT, renderIndicator);
    renderIndicator();
  }

  function initPageFeatures() {
    initSettingsPage();
    initIndicator();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPageFeatures, { once: true });
  } else {
    initPageFeatures();
  }
})();
